// Parser for hero description blocks with simple DSL
// Syntax:
// ::light(bg=#ffffff text=#0f172a accent=#f59e0b)
// ## Heading
// Paragraph lines...
// empty line separates paragraphs
// Supported types: light, dark, wide (default: light). `wide` blocks break out of
// the two-column grid and span the full container width.
// Returns null when no block markers are present (to allow fallback rendering)

export function parseHeroBlocks(raw, defaults = {}) {
  const source = String(raw || '').replace(/\r\n?|\n/g, '\n');
  if (!/\n?::(light|dark|wide)/i.test(source)) return null;

  const lines = source.split('\n');
  const result = [];

  let current = null;
  let currentParagraph = [];

  const pushParagraph = () => {
    const text = currentParagraph.join(' ').trim();
    if (text) current.paragraphs.push(text);
    currentParagraph = [];
  };

  const pushBlock = () => {
    if (current) {
      pushParagraph();
      result.push(current);
    }
  };

  const parseParams = (paramsStr) => {
    const colors = { bg: undefined, text: undefined, accent: undefined };
    if (!paramsStr) return colors;
    paramsStr.split(/[,\s]+/).forEach(pair => {
      const m = pair.match(/^(bg|text|accent)=(#[0-9a-fA-F]{3,8}|[a-zA-Z]+)$/);
      if (m) colors[m[1]] = m[2];
    });
    return colors;
  };

  for (const rawLine of lines) {
    const line = rawLine.trimEnd();
    const blockMatch = line.match(/^::(light|dark|wide)(?:\(([^)]*)\))?\s*$/i);
    if (blockMatch) {
      pushBlock();
      const type = blockMatch[1].toLowerCase();
      const colors = parseParams(blockMatch[2]);
      current = {
        type,
        title: '',
        paragraphs: [],
        colors
      };
      continue;
    }

    if (!current) {
      // Implicit first light block if text before markers
      current = { type: 'light', title: '', paragraphs: [], colors: {} };
    }

    const titleMatch = line.match(/^##\s*(.+)$/);
    if (titleMatch) {
      pushParagraph();
      current.title = titleMatch[1].trim();
      continue;
    }

    if (line.trim() === '') {
      pushParagraph();
      continue;
    }

    currentParagraph.push(line.trim());
  }

  pushBlock();

  // Apply defaults for colors
  const primary = defaults.primaryColor || '#f59e0b';
  return result.map(b => {
    const isDarkLike = b.type === 'dark' || b.type === 'wide';
    return {
      ...b,
      colors: {
        bg: b.colors.bg || (isDarkLike ? '#2f2f2f' : '#ffffff'),
        text: b.colors.text || (isDarkLike ? '#f8fafc' : '#0f172a'),
        accent: b.colors.accent || defaults.accentColor || primary,
      }
    };
  });
}


